import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['input', 'list', 'live', 'heading', 'chooseButton', 'content', 'submitButton'];

  connect() {
    this.stagedFiles = [];
    this.nameIdPrefix = `ac-${Math.random().toString(36).slice(2, 9)}`;
    this.form = this.element.closest('form');
    if (this.form) {
      this.boundOnSubmitEnd = this.onSubmitEnd.bind(this);
      this.form.addEventListener('turbo:submit-end', this.boundOnSubmitEnd);
    }
    // Find content input and submit button if not using targets
    this.contentInput = this.hasContentTarget
      ? this.contentTarget
      : this.form?.querySelector('[name="ai_assistant_message[content]"]');
    this.submitButton = this.hasSubmitButtonTarget
      ? this.submitButtonTarget
      : this.form?.querySelector('button[type="submit"]');
    if (this.contentInput) {
      this.boundContentInputHandler = this.onContentInput.bind(this);
      this.contentInput.addEventListener('input', this.boundContentInputHandler);
      this.onContentInput(); // set initial state
      // Prevent submit on Cmd+Enter if no text and no staged files
      this.boundKeydownHandler = (e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && !this.canSubmit()) {
          e.preventDefault();
        }
      };
      this.contentInput.addEventListener('keydown', this.boundKeydownHandler);
    }
    this.updateHeading();
  }

  disconnect() {
    if (this.form && this.boundOnSubmitEnd) {
      this.form.removeEventListener('turbo:submit-end', this.boundOnSubmitEnd);
    }
    if (this.contentInput && this.boundContentInputHandler) {
      this.contentInput.removeEventListener('input', this.boundContentInputHandler);
    }
    if (this.contentInput && this.boundKeydownHandler) {
      this.contentInput.removeEventListener('keydown', this.boundKeydownHandler);
    }
  }

  /** Backend allows file-only messages; UI must not require trimmed text when staged files exist. */
  canSubmit() {
    const hasText = Boolean(this.contentInput?.value?.trim());
    const hasFiles = this.stagedFiles.length > 0;
    return hasText || hasFiles;
  }

  onContentInput() {
    if (this.submitButton) {
      this.submitButton.disabled = !this.canSubmit();
    }
  }

  openPicker() {
    this.inputTarget.click();
  }

  filesChanged() {
    const incoming = Array.from(this.inputTarget.files || []);
    // Calculate combined size in bytes (existing + new)
    const MAX_SIZE = 50 * 1024 * 1024; // 50MB
    const totalSize =
      this.stagedFiles.reduce((sum, f) => sum + f.size, 0) +
      incoming.reduce((sum, f) => sum + f.size, 0);
    let errorMsg = null;
    if (incoming.length === 1 && incoming[0].size > MAX_SIZE) {
      errorMsg = `Files with more than 50 MB size are not allowed.`;
    } else if (totalSize > MAX_SIZE) {
      errorMsg = `The combined size of all attachments must not exceed 50 MB. Please remove some files and try again.`;
    }
    if (errorMsg) {
      if (window.Swal) {
        window.Swal.fire({
          icon: 'error',
          title: 'File size limit exceeded',
          html: errorMsg,
        });
      } else {
        alert(errorMsg);
      }
      this.inputTarget.value = '';
      return;
    }
    incoming.forEach((file) => this.stagedFiles.push(file));
    this.inputTarget.value = '';
    this.syncInputFromStaged();
    this.renderList();
    this.onContentInput();
    const n = this.stagedFiles.length;
    this.announce(
      n === 1
        ? `${this.stagedFiles[n - 1].name} added. ${n} file selected.`
        : `${incoming.length} file(s) added. ${n} files selected.`,
    );
    this.updateHeading();
  }

  remove(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10);
    if (Number.isNaN(index) || index < 0 || index >= this.stagedFiles.length) {
      return;
    }

    const removedName = this.stagedFiles[index].name;
    const nextFocusIndex = index < this.stagedFiles.length - 1 ? index : Math.max(0, index - 1);

    this.stagedFiles.splice(index, 1);
    this.syncInputFromStaged();
    this.renderList();
    this.updateHeading();
    this.onContentInput();

    const remaining = this.stagedFiles.length;
    this.announce(
      `${removedName} removed. ${remaining} file${remaining === 1 ? '' : 's'} remaining.`,
    );

    requestAnimationFrame(() => this.focusAfterRemove(nextFocusIndex));
  }

  focusAfterRemove(nextFocusIndex) {
    const buttons = this.listTarget.querySelectorAll('.ai-chat-attachments__remove');
    if (buttons.length === 0) {
      if (this.hasChooseButtonTarget) {
        this.chooseButtonTarget.focus();
      }
      return;
    }
    const btn = buttons[nextFocusIndex] || buttons[buttons.length - 1];
    if (btn) {
      btn.focus();
    }
  }

  syncInputFromStaged() {
    const dt = new DataTransfer();
    this.stagedFiles.forEach((f) => dt.items.add(f));
    this.inputTarget.files = dt.files;
  }

  renderList() {
    this.listTarget.innerHTML = '';
    this.stagedFiles.forEach((file, index) => {
      const li = document.createElement('li');
      li.className = 'ai-chat-attachments__item';
      li.setAttribute('role', 'listitem');

      // Format file size, e.g. "1.5 MB" or "876 KB"
      const formatFileSize = (bytes) => {
        if (bytes >= 1_000_000) {
          return `${(bytes / 1_000_000).toFixed(1)} MB`;
        } else if (bytes >= 1_000) {
          return `${Math.round(bytes / 1_000)} KB`;
        } else {
          return `${bytes} B`;
        }
      };

      const displayName = `${file.name} (${formatFileSize(file.size)})`;

      const name = document.createElement('span');
      name.className = 'ai-chat-attachments__name';
      name.id = `${this.nameIdPrefix}-f-${index}`;
      name.textContent = displayName;
      name.title = displayName;

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ai-chat-attachments__remove aq-card-close-btn fas fa-times delete-icon';
      btn.dataset.index = String(index);
      btn.setAttribute('aria-label', `Remove ${file.name} from attachments`);
      btn.setAttribute('aria-describedby', name.id);

      btn.addEventListener('click', (e) => this.remove(e));

      li.appendChild(name);
      li.appendChild(btn);
      this.listTarget.appendChild(li);
    });

    const hasFiles = this.stagedFiles.length > 0;
    this.listTarget.hidden = !hasFiles;
    this.listTarget.setAttribute('aria-hidden', hasFiles ? 'false' : 'true');
  }

  updateHeading() {
    if (!this.hasHeadingTarget) {
      return;
    }
    const n = this.stagedFiles.length;
    if (n === 0) {
      this.headingTarget.textContent = 'No files selected';
    } else {
      this.headingTarget.textContent = `Selected files, ${n} ${n === 1 ? 'item' : 'items'}`;
    }
  }

  announce(message) {
    if (!this.hasLiveTarget) {
      return;
    }
    this.liveTarget.textContent = '';
    requestAnimationFrame(() => {
      this.liveTarget.textContent = message;
    });
  }

  onSubmitEnd(event) {
    if (!event.detail.success) {
      return;
    }
    this.stagedFiles = [];
    this.syncInputFromStaged();
    this.renderList();
    this.updateHeading();
    this.onContentInput();
    this.announce('Files cleared after send.');
  }

  closeError(event) {
    const errorBox =
      this.targets.find('error') || event.currentTarget.closest('.ai-chat-form-errors');
    if (errorBox) {
      errorBox.style.display = 'none';
    }
  }
}
